import Pjax from './pjax/pjax';
import { requestJson } from '../utils/request';
import videos from '../../../parts/02_organisms/videos';

class PjaxHandler {
  constructor() {
    // DEFINE PROPERTIES
    this.queueUnloadFull = []; // Functions added to this queue will run each time the page does a full unload
    this.queueUnloadPartial = []; // Functions added to this queue will run each time the page does a partial unload
    this.loading = false;
    this.localStorageAvailable = false;
    this.articleRegister = []; // Local register of previously loaded articles

    // DEFINE GLOBALS
    window.pjaxHandler = this;
    window.triggerContinuousScroll = this.triggerContinuousScroll;

    // Run first-time set up
    this.setUpPjax();
    this.setUpListeners();
    this.verifyLocalStorageAvailability();
    this.setUpContinuousScrollIntersectionObserver();

    if (
      typeof window.currentInfinitePostId !== 'undefined' &&
      window.currentInfinitePostId !== null
    ) {
      this.addArticleToInfiniteList(window.currentInfinitePostId);
    } else {
      window.currentInfinitePostId = null;
    }
  }

  /*
   * Set up PJAX
   * -------------
   * Initialises a new instance of PJAX, with all required options set
   */
  setUpPjax = () => {
    window.pjax = new Pjax({
      elements: `a[href]:not(.pjax-exclude), a:not([target="_blank"]),form[action]`,
      selectors: [
        'head title',
        'meta',
        '#wpadminbar',
        '#declared_ad_slots',
        '#declared_analytics_data',
        '#declared_schema_json_markup',
        '#main-body-container',
        '.pm-ad-unit__is-1x1',
        '#nielsen_metadata_object',
        'link[rel="canonical"]',
      ],
      cacheBust: false,
      analytics: false,
      switches: {
        'head title': function(oldEl, newEl, options) {
          if (
            typeof options.isContinuous !== 'undefined' &&
            options.isContinuous === true
          ) {
            window.pjaxHandler.updateArticleRegister(
              options.postId,
              'title',
              newEl.innerHTML
            );
            window.pjaxHandler.updateArticleRegister(
              options.postId,
              'url',
              options.targetUrl
            );
          } else {
            oldEl.outerHTML = newEl.outerHTML;
          }
          this.onSwitch();
        },
        '#wpadminbar': function(oldEl, newEl, options) {
          if (
            typeof options.isContinuous !== 'undefined' &&
            options.isContinuous === true
          ) {
            window.pjaxHandler.updateArticleRegister(
              options.postId,
              'wpadminbar',
              newEl
            );
          } else {
            oldEl.outerHTML = newEl.outerHTML;
          }
          this.onSwitch();
        },
        '#declared_schema_json_markup': function(passedOldEl, newEl) {
          const oldEl = passedOldEl;
          const newData = newEl.text;
          oldEl.text = '';
          this.onSwitch();

          // PJAX tries to execute new scripts, which causes issues on data-blocks like this.
          // To stop this, we empty the data block, then insert it after 50ms - plenty of time to let PJAX have its fun
          setTimeout(() => {
            document.querySelector(
              '#declared_schema_json_markup'
            ).text = newData;
          }, 50);
        },
        '#main-body-container': function(oldEl, newEl, options) {
          if (
            typeof options.isContinuous !== 'undefined' &&
            options.isContinuous === true
          ) {
            window.pjaxHandler.updateArticleRegister(
              options.postId,
              'mainBodyContainer',
              newEl
            );
            window.pjaxHandler.insertArticleContainer(oldEl, newEl, options);
          } else if (
            options.isInifinity !== 'undefined' &&
            options.isInfinity === true
          ) {
            window.pjaxHandler.actionListingLoad(oldEl, newEl, options);
          } else {
            window.pjaxHandler.actionFullPageLoad(oldEl, newEl, options);
          }

          this.onSwitch();
        },
      },
    });
  };

  /*
   * Add an Article to the Article Register/Update an existing article
   * -------------
   * If the postID exists in the register, then update/append the values
   * Otherwise, create a new object to represent the article
   */
  updateArticleRegister = (postId, dataKey, dataVal) => {
    let index = this.articleRegister.findIndex(
      article => postId === article.postId
    );

    if (index === -1) {
      this.articleRegister.push({
        postId,
      });
    }

    index = this.articleRegister.findIndex(
      article => postId === article.postId
    );

    this.articleRegister[index][dataKey] = dataVal;
  };

  /*
   * Update static elements
   * --------------
   * Updates the title, URL and wpadminbar elements so that they match the data from articleRegister
   */
  updateStaticElements = postId => {
    const index = this.articleRegister.findIndex(
      article => postId === article.postId
    );

    const articleRegisterPost = this.articleRegister[index];
    if (!articleRegisterPost || typeof articleRegisterPost === 'undefined')
      return;

    const adminBar = document.querySelector(`#wpadminbar`);
    if (adminBar && articleRegisterPost.wpadminbar) {
      adminBar.outerHTML = articleRegisterPost.wpadminbar.outerHTML;
    }

    if (
      document.title &&
      articleRegisterPost.title &&
      document.title !== articleRegisterPost.title
    ) {
      document.title = articleRegisterPost.title;
    }

    // @codingStandardsIgnoreStart
    if (
      articleRegisterPost.url &&
      articleRegisterPost.url !== window.location.href
    ) {
      // @codingStandardsIgnoreEnd
      window.history.pushState(
        {
          url: articleRegisterPost.url,
          pageTitle: articleRegisterPost.title.innerHTML,
        },
        articleRegisterPost.title.innerHTML,
        articleRegisterPost.url
      );
    }
  };

  // Insert article container and convert old Main into a 'past-post'
  insertArticleContainer = (oldEl, newEl, options) => {
    const nextArticlePoint = document.querySelector(
      '.js-continuous-scroll-point'
    );
    const bankedNextPostId = options.bankedNextPostId;
    const mainContainer = document.querySelector(`#main-body-container`); // Main container of content
    const mainContainerContentContainer = mainContainer.querySelector(
      `#content-container`
    );
    const mainContentBodyOfNewPage = newEl.querySelector(`#main-content-body`);

    if (bankedNextPostId) {
      mainContentBodyOfNewPage.setAttribute(
        `data-next-post-ID`,
        bankedNextPostId
      );
    }

    mainContentBodyOfNewPage.classList.add('cs-loaded');

    const innerContentOfNewEl = mainContentBodyOfNewPage.outerHTML; // Main container in new article
    const currentArticle = document.querySelector('#main-content-body'); // Article which user is currently reading
    const storedAds = [];

    // First, push main into old articles
    currentArticle.removeAttribute('id');
    currentArticle.classList.add('js-old-stored-article');

    let postId = currentArticle.getAttribute('data-post-id');
    if (!postId) postId = false;

    nextArticlePoint.remove();

    const newArticle = document
      .createRange()
      .createContextualFragment(innerContentOfNewEl);

    // Then insert the new article
    mainContainerContentContainer.appendChild(newArticle);

    nextArticlePoint.dataset.isLoading = 'false';
    mainContainer.insertBefore(
      nextArticlePoint,
      innerContentOfNewEl.nextSibling
    );

    if (window.pjaxHandler.observerSupported) {
      window.pjaxHandler.stopObservingContinuousScroll();
      window.pjaxHandler.setUpContinuousScrollIntersectionObserver();
    }

    window.pjax.parseDOM(document);
  };

  // Fully replace the page
  actionFullPageLoad = (passedOldEl, newEl) => {
    const oldEl = passedOldEl;

    window.pjaxHandler.articleRegister = [];

    if (typeof window.AdManager !== 'undefined') {
      window.AdManager.unloadAllAds();
    }

    if (window.pjaxHandler.observerSupported) {
      window.pjaxHandler.stopObservingContinuousScroll();
    }

    // Kill all video players - new page won't need them
    window.videoPlayerManager.destroyAllPlayers();

    oldEl.outerHTML = newEl.outerHTML;
    if (window.pjaxHandler.observerSupported) {
      window.pjaxHandler.setUpContinuousScrollIntersectionObserver();
    }
  };

  // A listing load will fire when the user clicks on Load More articles from Home/Feed,etc
  actionListingLoad = (oldEl, newEl, options) => {
    const parentContainerId = options.parentContainerId;
    const infiniteContentListing = document.querySelector(
      `#${parentContainerId}`
    );
    const loadPoint = document.querySelector('#more-content-load-point');
    const mainContentOfNewEl = newEl.querySelector(
      '#archive-posts-container .row'
    );

    loadPoint.remove();

    if (!mainContentOfNewEl) return;

    infiniteContentListing.appendChild(mainContentOfNewEl);
    infiniteContentListing.appendChild(loadPoint);

    window.pjax.parseDOM(document);
  };

  // A full unload tries to clear out as much scripting as possible, making the next page feel as 'fresh' as possible
  actionFullUnload = () => {
    this.queueUnloadFull.forEach(unloadFunction => {
      if (typeof unloadFunction.func === 'function') {
        unloadFunction();
      }
    });

    this.queueUnloadFull = this.queueUnloadFull.filter(
      unloadFunction => unloadFunction.fireOnce !== true
    );
  };

  // A partial unload keeps most of the scripting in place, but resets the things we need
  actionPartialUnload = () => {
    this.queueUnloadPartial.forEach(unloadFunction => {
      if (typeof unloadFunction.func === 'function') {
        unloadFunction();
      }
    });

    this.queueUnloadPartial = this.queueUnloadPartial.filter(
      unloadFunction => unloadFunction.fireOnce !== true
    );
  };

  /* -----------------------------------------------------
   * Continuous Scroll Functionality
   * -----------------------------------------------------
   */

  triggerContinuousScroll = contScrollData => {
    const targetUrl = contScrollData.url;
    let bankedNextPostId = false;
    if (typeof contScrollData.bankedNextpostId !== 'undefined') {
      bankedNextPostId = contScrollData.bankedNextpostId;
    }

    let postId = false;
    if (typeof contScrollData.postId !== 'undefined') {
      postId = contScrollData.postId;
    }

    if (
      navigator.userAgent.indexOf('MSIE') !== -1 ||
      navigator.appVersion.indexOf('Trident/') > -1 ||
      this.getParameterByName('stopInfiniteScroll') ||
      this.getParameterByName('preview')
    ) {
      return;
    }

    window.pjax.loadUrl(targetUrl, {
      isContinuous: true,
      scrollTo: false,
      history: false,
      cacheBust: false,
      analytics: false,
      bankedNextPostId,
      postId,
      targetUrl,
    });
  };

  triggerInfinityListingLoad = passedElement => {
    const element = passedElement;
    const taxonomy = element.dataset.taxonomy;
    const term = element.dataset.term;
    const pageNumber = element.dataset.pageNumber;
    const parentContainerId = element.dataset.parentContainerId;

    element.dataset.pageNumber++;

    let siteUrl = `${window.location.protocol}//${window.location.host}${
      window.location.pathname
    }`;
    if (typeof window.siteData !== 'undefined' && window.siteData.siteUrl) {
      siteUrl = window.siteData.siteUrl;
    }

    const lastCharOfUrl = siteUrl[siteUrl.length - 1];
    if (lastCharOfUrl !== '/') {
      siteUrl += '/';
    }
    if (taxonomy && taxonomy !== 'category') {
      siteUrl += `${taxonomy}/`;
    }
    if (term) {
      siteUrl += `${term}/`;
    }
    if (pageNumber) {
      siteUrl += `page/${pageNumber}/`;
    }

    window.pjax.loadUrl(siteUrl, {
      isInfinity: true,
      scrollTo: false,
      history: false,
      analytics: false,
      parentContainerId,
      cacheBust: false,
    });
  };

  stopObservingContinuousScroll = () => {
    if (
      this.observerSupported &&
      typeof this.observer.disconnect === 'function'
    ) {
      this.observer.disconnect();
      console.log('successfully disconnected observer');
    }
  };

  setUpContinuousScrollIntersectionObserver = () => {
    if (
      navigator.userAgent.indexOf('MSIE') !== -1 ||
      navigator.appVersion.indexOf('Trident/') > -1 ||
      this.getParameterByName('stopInfiniteScroll') ||
      this.getParameterByName('preview')
    ) {
      const target = document.querySelector('.js-continuous-scroll-point');
      if (target) {
        target.style.opacity = 0;
      }
      return;
    }

    this.observerSupported = true;
    const target = document.querySelector('.js-continuous-scroll-point');
    const oldArticles = document.querySelectorAll('.js-old-stored-article');
    const mainArticle = document.querySelector('#main-content-body');

    try {
      const options = {
        root: null,
        rootMargin: '450px 0px 0px 0px',
        threshold: [
          0,
          0.05,
          0.1,
          0.15,
          0.2,
          0.25,
          0.3,
          0.4,
          0.5,
          0.6,
          0.7,
          0.75,
          0.8,
          0.85,
          0.9,
          0.95,
          1,
        ],
      };
      this.observer = new IntersectionObserver(
        window.pjaxHandler.intersectionContinuousScrollCallback,
        options
      );
    } catch (err) {
      this.observerSupported = false;
      if (target) target.remove();
    }

    if (this.observerSupported && target) {
      setTimeout(() => {
        window.pjaxHandler.observer.observe(target);
      }, 1000);
    }

    if (this.observerSupported && oldArticles) {
      setTimeout(() => {
        oldArticles.forEach(article => {
          window.pjaxHandler.observer.observe(article);
        });
      }, 10);
    }

    if (this.observerSupported && mainArticle) {
      setTimeout(() => {
        window.pjaxHandler.observer.observe(mainArticle);
      }, 10);
    }
  };

  intersectionContinuousScrollCallback = entries => {
    entries.forEach(entry => {
      if (
        entry.target.classList.contains('js-continuous-scroll-point') &&
        entry.isIntersecting &&
        entry.target.dataset.isLoading !== 'true'
      ) {
        entry.target.dataset.isLoading = 'true';
        const mainArticle = document.querySelector('#main-content-body');

        const nextPostIds = mainArticle.getAttribute('data-next-post-ID');
        let nextPostId = false;

        // 1. Convert nextPostIds into array
        // 2. Fetch stored values from localStorage
        // 3. Iterate over array. If nextPostID === false and the ID doesn't exist in localStorage, then set nextPostId
        const convertedNextPostIds = nextPostIds.split(',');
        let localStorageObject = {
          items: [],
        };
        if (this.localStorageAvailable) {
          const localStorageString = localStorage.getItem(
            'arn-infinite-scroll'
          );
          if (localStorageString) {
            try {
              localStorageObject = JSON.parse(localStorageString);
              localStorageObject.items.forEach((item, index) => {
                localStorageObject.items[index] = item.toString();
              });
            } catch (e) {
              console.log('local storage could not be parsed');
              this.setInitialLocalData('infinite');
            }
          } else {
            this.setInitialLocalData('infinite');
          }
        }
        convertedNextPostIds.forEach(item => {
          if (
            !nextPostId &&
            localStorageObject.items.indexOf(item.toString()) === -1
          ) {
            nextPostId = item.toString();
          }
        });
        // If all 10 posts have been seen, just fallback to the first post
        if (!nextPostId && convertedNextPostIds.length > 0) {
          nextPostId = convertedNextPostIds[0];
        }

        const originalNextPostId = nextPostId; // Keep a backup of it, in case we fetch a promoted post.

        const promotedPostId = this.fetchPromotedPost();
        if (promotedPostId) nextPostId = promotedPostId;

        // Fetch the URL for the next post from our REST API endpoint
        // If we're using a promoted post, nextPostId will === the promotedPostId
        // Otherwise it'll just equal the next post we think is most readable
        // If API returns false, then stop continuous scroll altogether
        if (nextPostId && nextPostId !== 'false' && !this.loading) {
          this.loading = true;

          let siteUrl = '';
          if (
            typeof window.siteData !== 'undefined' &&
            window.siteData.siteUrl
          ) {
            siteUrl = window.siteData.siteUrl;
          }
          siteUrl += `/wp-json/arn/v1/single/fetch/url?ID=${nextPostId}`;

          requestJson([siteUrl])
            .then(({ data }) => {
              console.log(data);
              this.loading = false;
              if (data) {
                const contScrollData = {
                  url: data,
                  postId: nextPostId,
                };
                if (originalNextPostId !== nextPostId) {
                  contScrollData.bankedNextpostId = originalNextPostId;
                }

                window.triggerContinuousScroll(contScrollData);
              }
            })
            .catch(() => {
              this.loading = false;
              document.querySelector('.js-continuous-scroll-point').remove();
            });
        } else if (!this.loading && (!nextPostId || nextPostId === 'false')) {
          document.querySelector('.js-continuous-scroll-point').remove();
        }
      }

      if (entry.target.classList.contains('js-old-stored-article')) {
        // Insert and remove posts
        if (entry.isIntersecting && entry.target.innerHTML === '') {
          this.insertPost(entry.target);
        } else if (!entry.isIntersecting && entry.target.innerHTML !== '') {
          this.unloadPost(entry.target);
        }
        window.pjax.parseDOM(document);
      }

      if (
        entry.target.classList.contains('js-old-stored-article') ||
        entry.target.id === 'main-content-body'
      ) {
        // Modify static components of site (title, url, etc)

        if (entry.isIntersecting) {
          const screenHeight = screen.height;
          const screenPosition = window.scrollY;
          const rect = entry.target.getBoundingClientRect();

          const lowBound = screenPosition; // The maximum value that the element can have before it's top is in view
          const highBound = screenPosition + screenHeight; // The minimum value the element can have before its bottom is in view

          const elementLow = rect.top + screenPosition; // The top-position of the element relative to the top of the document
          const elementHigh = rect.top + screenPosition + rect.height; // The bottom-position of the element relatie to the top of the document

          // Is the element the focus of the view
          let viewFocus = false;

          if (elementLow < lowBound && elementHigh > highBound) {
            viewFocus = true;
          } else if (elementLow < lowBound && elementHigh < highBound) {
            const diff = highBound - elementHigh; // The difference between the bottom bound and bottom position
            const percentage = (screenHeight - diff) / screenHeight;
            if (percentage > 0.5) {
              viewFocus = true;
            }
          } else if (elementLow > lowBound && elementHigh > highBound) {
            const diff = elementLow - lowBound; // The difference between the top bound and top position
            const percentage = (screenHeight - diff) / screenHeight;
            if (percentage > 0.5) {
              viewFocus = true;
            }
          }

          if (viewFocus) {
            this.updateStaticElements(entry.target.dataset.postId);
          }
        }
      }
    });
  };

  // Remove a post from the DOM
  unloadPost = target => {
    const targetDiv = target;
    const mainContainer = document.querySelector('#main-body-container');
    const mainArticle = document.querySelector('#main-content-body');

    if (!mainContainer) return;
    if (!mainArticle) return;

    let storedAds = [];

    const postId = targetDiv.dataset.postId;
    const uniqueIdentifier = targetDiv.dataset.uniqueIdentifier;
    const index = this.articleRegister.findIndex(
      article => postId === article.postId
    );
    const selectedPost = this.articleRegister[index];

    if (typeof window.AdManager !== 'undefined') {
      storedAds = window.AdManager.unloadAdsWithIdentifier(
        uniqueIdentifier,
        () => {
          setTimeout(() => {
            window.pjaxHandler.updateStoredDomAndClearElement(uniqueIdentifier);
          }, 200);
        }
      );

      const cleanedArticle = targetDiv.innerHTML
        .replace(/data-pjax-state/g, '')
        .replace(/twitter-tweet-rendered/g, '');

      // TODO - clean this up a bit
      const articleUrl = targetDiv.dataset.currUrl;
      const articleTitle = targetDiv.dataset.currTitle;
      const articleId = targetDiv.dataset.postId;
      window.pjaxHandler.updateArticleRegister(
        articleId,
        'title',
        articleTitle
      );
      window.pjaxHandler.updateArticleRegister(articleId, 'url', articleUrl);

      window.pjaxHandler.updateArticleRegister(postId, 'dom', cleanedArticle);
      window.pjaxHandler.updateArticleRegister(postId, 'storedAds', storedAds);
    }

    // Disable video player, if it exists
    window.videoPlayerManager.destroyPlayer(`video_player${uniqueIdentifier}`);
  };

  // Insert a post into the DOM from articleRegister
  insertPost = passedTarget => {
    const target = passedTarget;
    const postId = target.dataset.postId;
    const index = this.articleRegister.findIndex(
      article => postId === article.postId
    );
    const selectedPost = this.articleRegister[index];

    const mainContainer = document.querySelector('#main-body-container');
    const mainArticle = document.querySelector('#main-content-body');

    if (!mainContainer) return;
    if (!mainArticle) return;

    if (selectedPost) {
      target.innerHTML = selectedPost.dom;
      target.style.height = 'auto';

      selectedPost.storedAds.forEach(storedAd => {
        storedAd.metadata.loaded = false;
        window.AdManager.unactionedAdSlots.push(storedAd);
      });

      window.AdManager.setup();
      window.arnPageViewInit();
    }
  };

  // Updates the DOM with attribute postId === targetId
  updateStoredDomAndClearElement = targetId => {
    const domElement = document.querySelector(
      `.js-old-stored-article[data-unique-identifier='${targetId}']`
    );
    if (domElement) {
      const heightVal = domElement.offsetHeight;
      domElement.style.height = `${heightVal}px`;
      domElement.innerHTML = '';
    }
  };

  /*
   * Promoted Content Functionality
   */
  fetchPromotedPost = () => {
    // Promotional Post AJAXing here.
    // We check the promoted post ID and frequency cap against the user's localStorage
    // If ID matches and time is outside cut-off value, then lets bring in the promoted post
    // Otherwise, let's not
    let nextPostId = false;
    let promotedPostId = false;
    if (window.promotedPostSettings && this.localStorageAvailable) {
      console.log(document.referrer);

      const localStorageString = localStorage.getItem('arn-promoted-post');
      let localStorageObject = {
        ID: 0,
        lastViewed: 0,
      };
      if (localStorageString) {
        try {
          localStorageObject = JSON.parse(localStorageString);
        } catch (e) {
          console.log('local storage could not be parsed');
          this.setInitialLocalData('promoted');
        }
      } else {
        this.setInitialLocalData('promoted');
      }

      const mainArticle = document.querySelector('#main-content-body');
      if (!mainArticle) return;

      const thisPostId = mainArticle.getAttribute('data-post-ID');

      let hasSet = false;

      if (
        window.promotedPostSettings.ID &&
        window.promotedPostSettings.frequencyCap &&
        window.promotedPostSettings.ID === localStorageObject.ID
      ) {
        const dateNow = new Date();
        const timeNow = dateNow.getTime();
        let difference = 0;

        switch (window.promotedPostSettings.frequencyCap) {
          case 'no-cap':
            promotedPostId = window.promotedPostSettings.ID;
            hasSet = true;
            break;
          case 'once-per-hour':
            difference = 1000 * 60 * 60;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-3-hours':
            difference = 1000 * 60 * 60 * 3;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-6-hours':
            difference = 1000 * 60 * 60 * 6;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-12-hours':
            difference = 1000 * 60 * 60 * 12;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-day':
            difference = 1000 * 60 * 60 * 24;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-2-days':
            difference = 1000 * 60 * 60 * 24 * 2;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-3-days':
            difference = 1000 * 60 * 60 * 24 * 3;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-5-days':
            difference = 1000 * 60 * 60 * 24 * 5;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-week':
            difference = 1000 * 60 * 60 * 24 * 7;
            if (timeNow - localStorageObject.lastViewed > difference) {
              promotedPostId = window.promotedPostSettings.ID;
              hasSet = true;
            }
            break;
          case 'once-per-lifetime':
          default:
            // Do nothing
            break;
        }
      } else if (
        window.promotedPostSettings.ID &&
        window.promotedPostSettings.frequencyCap &&
        window.promotedPostSettings.ID !== localStorageObject.ID
      ) {
        promotedPostId = window.promotedPostSettings.ID;
        hasSet = true;
      }

      // If the user has already naturally stumbled upon this article recently, we don't want to show it to them again.
      // Without this, the no-cap setting would show only the promoted article
      if (hasSet && this.localStorageAvailable) {
        const dateNow = new Date();

        let hasAlreadySeen = false;
        if (promotedPostId && thisPostId === promotedPostId) {
          hasAlreadySeen = true;
        }
        this.articleRegister.forEach(article => {
          if (promotedPostId && article.postId === promotedPostId) {
            hasAlreadySeen = true;
          }
        });

        if (!hasAlreadySeen) {
          nextPostId = promotedPostId;
        }

        const storageString = JSON.stringify({
          ID: promotedPostId,
          lastViewed: dateNow.getTime(),
        });

        localStorage.setItem('arn-promoted-post', storageString);
      }
    }

    return nextPostId;
  };

  setInitialLocalData = storageType => {
    if (this.localStorageAvailable) {
      const localStorageObject = {};
      let keyName = 'pjax';
      if (storageType === 'promoted') {
        keyName = 'arn-promoted-post';
        localStorageObject.ID = 0;
        localStorageObject.lastViewed = 0;
      } else if (storageType === 'infinite') {
        keyName = 'arn-infinite-scroll';
        localStorageObject.items = [];
      }
      localStorage.setItem(keyName, JSON.stringify(localStorageObject));
    }
  };

  verifyLocalStorageAvailability = () => {
    const testData = 'validateLocalStorage';
    try {
      localStorage.setItem(testData, testData);
      localStorage.removeItem(testData);
      this.localStorageAvailable = true;
    } catch (e) {
      console.log(e);
      this.localStorageAvailable = false;
    }
  };

  // Adds an article ID to the infinite scroll list, to prevent it from appearing again
  // Prevents this list from getting excessive by limiting it to 50 items
  addArticleToInfiniteList = ID => {
    if (this.localStorageAvailable) {
      let localStorageObject = {
        items: [],
      };
      const localStorageString = localStorage.getItem('arn-infinite-scroll');
      if (localStorageString) {
        try {
          localStorageObject = JSON.parse(localStorageString);
          if (!Array.isArray(localStorageObject.items)) {
            localStorageObject = {
              items: [],
            };
          }
        } catch (e) {
          console.log('local storage could not be parsed');
          this.setInitialLocalData('infinite');
        }
      } else {
        this.setInitialLocalData('infinite');
      }

      if (localStorageObject.items.indexOf(ID.toString()) === -1) {
        localStorageObject.items.unshift(ID.toString());
      }
      if (localStorageObject.items.length >= 50) {
        localStorageObject.items.pop();
      }

      localStorage.setItem(
        'arn-infinite-scroll',
        JSON.stringify(localStorageObject)
      );
    }
  };

  getParameterByName = (passedName, passedUrl) => {
    let url = passedUrl;
    let name = passedName;
    if (!url) {
      url = window.location.href;
    }
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  /*
   * Event Listeners
   * --------------
   * PJAX fires specific events on success, failure and start. We can trigger events to occur off the back of these
   */
  setUpListeners = () => {
    document.addEventListener('pjax:send', this.pjaxStart);
    document.addEventListener('pjax:error', this.pjaxError);
    document.addEventListener('pjax:success', this.pjaxSuccess);
  };

  pjaxStart = e => {
    console.log(e);
    console.log('===================== PJAX START ========================');
  };

  pjaxError = () => {
    console.error('===================== PJAX ERROR ========================');
  };

  pjaxSuccess = () => {
    this.previousPageCleanUp();
    console.log('===================== PJAX SUCCESS ========================');
    window.arnPageViewInit();
    window.ArnAnalytics.niesenNSETracking();
    videos(); // Re-initialise videos functionality
    if(typeof window.songList !== 'undefined') {
      window.songList();
    }
    if(typeof window.youtubeVideos !== 'undefined') {
      window.youtubeVideos();
    }
    if (typeof window.AdManager !== 'undefined') {
      window.AdManager.setup(); // Re-initialise ads each time
    }

    // Update Aptivada form script
    if (document.getElementsByClassName('aptivada-widget').length !== 0) {
      if(document.getElementById("aptivada-script") === null) {
        if( window.Aptivada) {
          window.Aptivada.widgets = [];
          window.Aptivada.loaded = false;
        }

        window.aptivadaScriptTag = document.createElement("script");
        aptivadaScriptTag.id = 'aptivada-script';
        aptivadaScriptTag.src = "https://www.aptivada.com/js/widget.js";
        document.head.appendChild(aptivadaScriptTag);
      }
    } else {
      if(document.getElementById("aptivada-script") !== null) {
        document.getElementById("aptivada-script").remove();
      }
    }

    //involke Oovvuu player
    if (typeof oovvuuRuntime === 'function') {
        oovvuuRuntime();
    }

    // // force load instagram embed scripts
    // if ( document.getElementsByTagName('article').length !== 0 ) {
    //     window.instagramScriptTag = document.createElement("script");
    //     instagramScriptTag.src = "https:////www.instagram.com/embed.js";
    //     document.body.appendChild(instagramScriptTag);
    //     instagramScriptTag.onload = function () {
    //       window.instgrm.Embeds.process();
    //     }
    // }


    //window.setAudioPlayerExpandState('minified');
    window.ARN.Components.OffCanvasNav.hideDimmer();
    window.ARN.Components.OffCanvasNav.closeNav();

    this.kiisBomb();
    this.cardReveal();
    this.insertPodcasts();
    this.iHeartDownloadModal();
    // this.generateIframeParam();
  };

  kiisBomb = () => {

    function rollthedice(chance) {
      if (chance > 1) {
        alert("Chance cannot be greater than 1.");
        return false;
      }
      const num = Math.random();
      console.log(num);
      if (num < chance) return true;
      return false;
    }
    const url = window.location.href;
    let timeWindow = false;
    const time = Date.now();
    if (time > 1590696000000 && time < 1590724800000) timeWindow = true;

    if (url.search('kiis1065') !== -1 && timeWindow) {
      console.log('conditions met for pjax firing');
      document.getElementById("bomb-container").classList.remove("bomb-container-revealed");
      document.getElementById("bomb-container").classList.add("bomb-container-hidden");
      const chance = 0.33;
      if (rollthedice(chance) === true) {
        console.log('win');
        // document.getElementById('bomb-container').style.visibility = 'visible';
        document.getElementById("bomb-container").classList.remove("bomb-container-hidden");
        document.getElementById("bomb-container").classList.add("bomb-container-revealed");
      }
    }
  };

  cardReveal = () => {
    console.log('window loaded---------------------------------------');

    const changeSubtitle = function(id) {
      const numberID = 'key-' + id;
      const captionID = 'caption-' + id;
      document.getElementById(numberID).style.display = 'none';
      document.getElementById(captionID).style.display = 'block';
    };

    const hideCoverImage = function() {
      this.style.opacity = 1;
      console.log('clicked');
      const interval = setInterval(() => {
        if (this.style.opacity > 0) this.style.opacity -= 0.1;
        else {
          this.style.display = 'none';
          clearInterval(interval);
        }
      }, 50);
      const number = this.id;
      changeSubtitle(number);
    };

    const elements = document.getElementsByClassName('vault-door');
    for (let i = 0; i < elements.length; i++) {
      elements[i].addEventListener('click', hideCoverImage);
    }
  };

  insertPodcasts = () => {
    let articles = document.getElementsByClassName('article-body');
    if(articles && articles.length > 0) {
      for (let i = 0; i < articles.length; i++) {
        let existingiframe = false;
        const iframes = articles[i].getElementsByTagName('iframe');
        for (let j = 0; j < iframes.length; j++) {
          if (!iframes[j].classList.contains('iHeart-default-podcast-iframe')) {
            const src = iframes[j].getAttribute('src');
            if (src) {
              const srcSplit = src.split(/https:\/\/|\?/);
              if (srcSplit.length > 1 && srcSplit[1] === 'playlist.megaphone.fm') {
                existingiframe = true;
              } else if (iframes[j].getAttribute('src').includes('iheart.com/podcast/')) {
                existingiframe = true;
              }
            }
          }
        }

        console.log('existingiframe', existingiframe);
        if (existingiframe === false && articles[i].querySelector('.iHeart-default-podcast')) {
          articles[i]
            .querySelector('.iHeart-default-podcast')
            .classList.add('visible');
        }
      }
    }
  };

  generateIframeParam = () => {
    const r = Math.floor(Math.random() * 1000000);
    const station = document.getElementById('iheart-iframe');
    const stationPName = station ? station.name : '';
    document.getElementById(
      'iheart-iframe'
    ).src = `https://www.iheart.com/favorites/your-1455392486/?embed=true&sc=ARN_Station_Website&pname=${stationPName}&correlator=${r}`;
  };

  iHeartDownloadModal = () => {

    function setBootstrapModal() {

      let triggerContainer = document.querySelector('.iHeart-modal-popup');

      if (triggerContainer) {
        let closeButton = document.querySelector('#close-button');

        let userAgentDevice = verifyUserDevice();

        let modal = getCookie("modal");
        let container = document.querySelector('#modal') !== null;

        /*Check if modal cookie is set or not*/
        if (modal !== "") {
          /*if cookie is set lets hide the modal*/

          if (container) {
            document.querySelector('#modal').style.display = 'none';
          }
        } else {
          /*if cookie is not set lets show the modal for once*/

          if (container && userAgentDevice === true) {
            document.querySelector('#modal').style.display = 'block';

            closeButton.addEventListener('click', () => {
              document.querySelector('#modal').style.display = 'none';
            });

            /**
             * setCookie(cname, cvalue, exdays)
             * cookie expires in 7 days
             */
            setCookie("modal", 'hide', 365);
          }
        }
      }
    }

    function setCookie(cname, cvalue, exdays) {
      let d = new Date();
      d.setTime(d.getTime() + (exdays*24*60*60*1000));
      let expires = "expires="+ d.toUTCString();
      document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    }

    function getCookie(cname) {
      let name = cname + "=";
      let decodedCookie = decodeURIComponent(document.cookie);
      let x = decodedCookie.split(';');

      for(let i = 0; i < x.length; i++) {
        let c = x[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length);
        }
      }
      return "";
    }

    function verifyUserDevice() {
      const toMatch = [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i
      ];

      return toMatch.some((toMatchItem) => {
        return navigator.userAgent.match(toMatchItem);
      });
    }
    /*
    Calling to function
     */
    setBootstrapModal();
  }

  /**
   * Clean up generated html elements before rendering next pjax loaded page
   */
  previousPageCleanUp = () => {

    if (typeof window.AdManager !== 'undefined') {
      window.AdManager.unloadLabeledAds();
    }

    const bgAdElement = document.querySelector(`#bg_ad`);

    if (bgAdElement) {
      bgAdElement.remove();
    }

    const leftAdElement = document.querySelector(`.left_ad`);
    if (leftAdElement) {
      leftAdElement.remove();
    }
    const rightAdElement = document.querySelector(`.right_ad`);
    if (rightAdElement) {
      rightAdElement.remove();
    }

  }
}

export default () => new PjaxHandler();
